// 지표 한 벌 — 좁으면 2열, 넓으면 한 줄
//
// ── 왜 이것이 공용 조각인가 ─────────────────────────────────────────────────
//
// 「이름 하나에 숫자 몇 개」라는 같은 모양이 시퀀스 목록과 수비 화면 둘(구성, 열람)에 있다
// (2026-08-27 까지는 구성 화면의 attack 카드도 썼다). 두 곳이
// 따로 배치를 적었더니 좁은 폭에서 서로 다르게 무너졌고, 한쪽을 고쳐도 다른 쪽은 그대로였다.
// 실제로 2026-08-13 관측이 미니 시퀀스를 잡았고 이어진 회차가 같은 결함을 attack 카드의
// 390~420픽셀 밴드에서 다시 잡았다. 배치를 한 자리로 모으면 그 되풀이가 끝난다.
//
// ── 무엇을 막는가 ───────────────────────────────────────────────────────────
//
// 라벨과 값을 형제 항목으로 늘어놓으면, 폭이 줄 때 **라벨 칸이 먼저 짓눌린다.** 한국어는
// 음절 사이가 전부 줄바꿈 자리라 짓눌린 칸의 최소 너비가 한 글자가 되고, 그래서 `개시 Δ` 가
// 개/시/Δ 로 세로 막대가 됐다. 음절 쪼개짐 자체는 전역 CSS 의 `word-break: keep-all` 이
// 막는다. 이 파일은 그 위층을 맡는다 — **라벨과 값을 한 칸에 묶어 그 칸이 통째로 접히게**
// 한다. 묶지 않으면 라벨만 다음 줄로 떨어져 나가 그 숫자의 이름이 화면에서 사라진다.
//
// ── 왜 하필 2열인가 ─────────────────────────────────────────────────────────
//
// 한 열로 세우면 rally 한 줄이 네 줄 높이가 되어 340픽셀에서 세 줄만 쌓여도 화면을 넘긴다.
// 세 열로 세우면 각 칸이 100픽셀 아래로 떨어져 라벨과 값이 다시 붙어 버린다. 2열은 칸마다
// 라벨과 값이 나란히 설 폭을 주면서 rally 한 줄을 두 줄 높이로 묶는 유일한 자리다.
//
// 값을 칸의 오른쪽에 붙이는 이유는 이 목록이 **비교하는 목록**이기 때문이다. 오른쪽 끝에
// 맞춰야 자릿수가 다른 숫자끼리도 같은 자리에서 끝나고, 그래야 눈이 세로로 훑어 내려간다.

import type React from 'react'

/**
 * 한 켜로 펴지는 분기점별 class 묶음.
 *
 * **문자열을 조립하지 않고 미리 적어 둔다.** Tailwind 는 소스에 그대로 적힌 class 이름만
 * 걷어 가므로 `` `${bp}:contents` `` 처럼 만들어 낸 이름은 만들어진 CSS 에 아예 없다.
 * 붙는 자리가 배치라 안 붙어도 화면이 조용히 어긋날 뿐 오류가 나지 않는다.
 *
 * 분기점이 둘인 이유는 **칸이 펴질 폭이 화면 폭이 아니라 그 목록이 앉은 상자의 폭**이기
 * 때문이다. 열람 defense 의 roster 섹션은 화면 전폭을 쓰므로 768 에서 이미 넉넉하지만
 * (2026-08-27 까지는 attack 카드가 그 자리였다), 시퀀스 목록은 그
 * 폭에서 편집기가 이미 2열로 갈라져 절반만 받는다. 실제로 768 에서 표의 고정 칸들이 폭을
 * 다 먹고 이름 칸에 0픽셀을 남겨 이름이 세로로 쪼개졌다. (2026-08-14 관측.)
 */
const WIDE_AT = {
  md: { host: 'md:contents', hide: 'md:hidden', end: 'md:justify-end', start: 'md:justify-start' },
  lg: { host: 'lg:contents', hide: 'lg:hidden', end: 'lg:justify-end', start: 'lg:justify-start' },
} as const

export interface Figure {
  /** 이 숫자의 이름. 좁은 폭에서는 값 옆에 붙고, 표 머리가 있는 곳에서는 넓어지면 감춰진다. */
  label: string
  value: React.ReactNode
  /** 값에 붙는 표식. **라벨에는 붙이지 않는다** — 붙이면 값을 재는 단언이 라벨까지 읽는다. */
  testId?: string
  /** 넓은 폭에서 이 칸이 받을 너비. 표 머리와 폭을 맞추는 자리라 부르는 쪽이 정한다. */
  wide?: string
}

export interface FiguresProps {
  figures: readonly Figure[]
  /**
   * 넓은 폭에서의 모양.
   *
   * `table` — 위에 표 머리가 있어 라벨을 그쪽이 지고 있다. 넓어지면 라벨을 감추고 값만
   * 오른쪽 정렬로 세워 머리 아래 칸을 맞춘다.
   *
   * `inline` — 표 머리가 없다. 넓어져도 라벨을 값 옆에 그대로 두어야 그 숫자가 무엇인지가
   * 화면에 남는다.
   */
  wide: 'table' | 'inline'
  /**
   * 한 켜로 펴질 분기점. **화면 폭이 아니라 이 목록이 앉은 상자의 폭으로 고른다** — 화면
   * 전폭을 쓰는 목록은 `md`, 이미 갈라진 열 안에 앉은 목록은 `lg`.
   */
  at: 'md' | 'lg'
  /**
   * 바깥 상자에 얹을 것. **어느 자리에 놓일지는 부르는 쪽이 안다** — 미니 시퀀스는 격자 안의
   * 둘째 줄이고 attack 카드는 감싼 flex 에서 자기 줄이라, 자리 지정을 이 파일이 정할 수 없다.
   */
  className?: string
}

/**
 * 지표 칸들을 그린다.
 *
 * 좁은 폭에서는 스스로 2열 격자가 되고, 넓어지면 `display: contents` 로 **배치에서 사라져**
 * 칸들이 감싼 줄의 직접 항목이 된다. 사라지게 하는 이유는 넓은 폭의 한 줄이 이름·숫자들·
 * 삭제 마크를 한 켜에 세우기 때문이다. 중간 상자가 남아 있으면 숫자들이 자기들끼리 한
 * 덩어리로 묶여 표 머리의 칸 너비와 어긋난다.
 *
 * 감싸개를 `span` 으로 두는 것은 접근성 때문이다. `display: contents` 는 목록·표처럼 의미를
 * 가진 원소에 얹으면 낭독기에서 그 의미가 사라지는데, `span` 은 원래 의미가 없어 잃을 것이
 * 없다.
 */
export function Figures(props: FiguresProps): React.ReactElement {
  const at = WIDE_AT[props.at]
  const wideAlign = props.wide === 'table' ? at.end : at.start

  // D4 방향 c: 표 머리가 없는 목록(inline)은 폭과 무관하게 계기 칸(라벨 위, 값 아래)으로 그린다. 순서와 글자는 그대로다.
  if (props.wide === 'inline') {
    return (
      <span className={`metrics ${props.className ?? ''}`}>
        {props.figures.map((figure) => (
          <span key={figure.label} className="metric">
            <span className="metric-label">{figure.label}</span>
            <span data-testid={figure.testId} className="metric-value font-mono">
              {figure.value}
            </span>
          </span>
        ))}
      </span>
    )
  }

  return (
    <span
      className={`grid grid-cols-2 items-baseline gap-x-3 gap-y-tight ${at.host} ${props.className ?? ''}`}
    >
      {props.figures.map((figure) => (
        <span
          key={figure.label}
          className={`flex min-w-0 items-baseline justify-between gap-1.5 ${wideAlign} ${figure.wide ?? ''}`}
        >
          {/* **라벨은 절대 접히지 않는다.** 접히는 순간 이 파일이 막으려던 세로 막대가 된다.
              칸이 모자라면 라벨이 줄어드는 대신 값 쪽이 좁아지는데, 값은 일곱 글자 남짓이라
              2열 폭에서 좁아질 일이 없다. */}
          <span
            className={`shrink-0 whitespace-nowrap text-xs text-ink-mute ${
              props.wide === 'table' ? at.hide : ''
            }`}
          >
            {figure.label}
          </span>
          <span data-testid={figure.testId} className="font-mono font-medium tabular-nums">
            {figure.value}
          </span>
        </span>
      ))}
    </span>
  )
}
